<script setup>
// NOTICE: KAPTION-INTEGRATION — Unified filter dropdown for the reports dashboard.
// One component drives every dropdown filter (inbox single-select, campaign
// multi-select) so they share an IDENTICAL pill trigger + dropdown panel — same
// border, radius, padding, shadow, hover/focus states. Mirrors the components-next
// combobox patterns (OnClickOutside, search input, tag chips) but adds optional
// type badges, which the generic combobox dropdown can't render.
import { ref, computed, watch, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // Array of { value, label, type? }. `type` ('direct' | 'campaign') drives the badge.
  options: {
    type: Array,
    default: () => [],
    validator: value =>
      value.every(option => 'value' in option && 'label' in option),
  },
  // String/Number in single mode, Array in multiple mode.
  modelValue: {
    type: [String, Number, Array],
    default: '',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyState: {
    type: String,
    default: '',
  },
  // Leading icon on the trigger (e.g. 'i-lucide-inbox', 'i-lucide-megaphone').
  icon: {
    type: String,
    default: '',
  },
  // Render a campaign-type badge on each option / tag.
  showBadge: {
    type: Boolean,
    default: false,
  },
  // Which trigger edge the panel lines up with. Use 'end' for filters at the
  // right of a scroll container so the panel does not overflow it.
  align: {
    type: String,
    default: 'start',
    validator: value => ['start', 'end'].includes(value),
  },
  // Short option lists do not need a search box.
  searchable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const internalValue = ref(
  props.multiple ? [...(props.modelValue || [])] : props.modelValue
);
const open = ref(false);
const search = ref('');
const searchInput = ref(null);

const filteredOptions = computed(() => {
  const term = search.value.toLowerCase();
  return props.options.filter(option =>
    option.label?.toLowerCase().includes(term)
  );
});

const selectedTags = computed(() => {
  if (!props.multiple) return [];
  return internalValue.value
    .map(value => props.options.find(option => option.value === value))
    .filter(Boolean);
});

const isSelected = option => {
  if (props.multiple) return internalValue.value.includes(option.value);
  return internalValue.value === option.value;
};

const triggerLabel = computed(() => {
  if (props.multiple) {
    if (selectedTags.value.length === 0) return props.placeholder;
    if (selectedTags.value.length === 1) return selectedTags.value[0].label;
    return t('CAMPAIGN.REPORT.DASHBOARD.FILTER_CAMPAIGNS_COUNT', {
      count: selectedTags.value.length,
    });
  }
  const selected = props.options.find(
    option => option.value === internalValue.value
  );
  return selected?.label || props.placeholder;
});

const showCountBadge = computed(
  () => props.multiple && selectedTags.value.length > 1
);

const isDirect = type => type === 'direct';
const typeLabel = type =>
  isDirect(type)
    ? t('CAMPAIGN.REPORT.LEADERBOARD.TYPE_DIRECT')
    : t('CAMPAIGN.REPORT.LEADERBOARD.TYPE_CAMPAIGN');

const selectOption = option => {
  if (props.multiple) {
    const index = internalValue.value.indexOf(option.value);
    if (index === -1) internalValue.value.push(option.value);
    else internalValue.value.splice(index, 1);
    emit('update:modelValue', [...internalValue.value]);
  } else {
    internalValue.value = option.value;
    emit('update:modelValue', option.value);
    open.value = false;
    search.value = '';
  }
};

const removeTag = value => {
  const index = internalValue.value.indexOf(value);
  if (index !== -1) {
    internalValue.value.splice(index, 1);
    emit('update:modelValue', [...internalValue.value]);
  }
};

const clearAll = () => {
  internalValue.value = [];
  emit('update:modelValue', []);
};

const toggleDropdown = () => {
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    nextTick(() => searchInput.value?.focus());
  }
};

// Escape closes only this panel, not a dialog that hosts the filter.
const closeOnEscape = event => {
  if (!open.value) return;
  event.stopPropagation();
  open.value = false;
};

watch(
  () => props.modelValue,
  newValue => {
    internalValue.value = props.multiple ? [...(newValue || [])] : newValue;
  }
);
</script>

<template>
  <div class="relative" @keydown.esc="closeOnEscape">
    <OnClickOutside @trigger="open = false">
      <!-- Trigger — identical pill style across all filters -->
      <button
        type="button"
        class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border bg-n-alpha-black2 transition-colors"
        :class="
          open
            ? 'border-n-brand text-n-slate-12'
            : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-black4'
        "
        @click="toggleDropdown"
      >
        <span v-if="icon" class="w-3.5 h-3.5 text-n-slate-10" :class="icon" />
        <span class="max-w-[12rem] truncate">{{ triggerLabel }}</span>
        <span
          v-if="showCountBadge"
          class="flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-bold rounded-full bg-n-brand text-white"
        >
          {{ selectedTags.length }}
        </span>
        <span
          class="w-3.5 h-3.5 text-n-slate-9"
          :class="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        />
      </button>

      <!-- Dropdown panel — identical chrome across all filters -->
      <div
        v-show="open"
        class="absolute z-50 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-n-strong bg-n-solid-1 shadow-lg"
        :class="align === 'end' ? 'end-0' : 'start-0'"
      >
        <!-- Search -->
        <div v-if="searchable" class="relative border-b border-n-strong">
          <span
            class="absolute i-lucide-search top-2.5 size-4 left-3 text-n-slate-10"
          />
          <input
            ref="searchInput"
            v-model="search"
            type="search"
            :placeholder="searchPlaceholder"
            class="reset-base w-full py-2 pl-10 pr-2 text-sm border-none rounded-t-lg bg-n-solid-1 text-n-slate-12 focus:outline-none"
          />
        </div>

        <!-- Selected summary / clear (multiple only) -->
        <div
          v-if="multiple && selectedTags.length > 0"
          class="flex items-center justify-between px-3 py-1.5 border-b border-n-weak"
        >
          <span class="text-xs text-n-slate-10">
            {{
              t('CAMPAIGN.REPORT.DASHBOARD.FILTER_CAMPAIGNS_COUNT', {
                count: selectedTags.length,
              })
            }}
          </span>
          <button
            type="button"
            class="text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
            @click="clearAll"
          >
            {{ t('CAMPAIGN.REPORT.DASHBOARD.FILTER_CLEAR') }}
          </button>
        </div>

        <!-- Options -->
        <ul
          class="py-1 mb-0 overflow-auto max-h-60"
          role="listbox"
          :aria-multiselectable="multiple"
        >
          <li
            v-for="option in filteredOptions"
            :key="option.value"
            class="flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': isSelected(option) }"
            role="option"
            :aria-selected="isSelected(option)"
            @click="selectOption(option)"
          >
            <span class="flex items-center gap-2 min-w-0">
              <!-- Multi: checkbox · Single: radio dot -->
              <span
                v-if="multiple"
                class="w-4 h-4 shrink-0 rounded border flex items-center justify-center"
                :class="
                  isSelected(option)
                    ? 'bg-n-brand border-n-brand'
                    : 'border-n-strong'
                "
              >
                <span
                  v-if="isSelected(option)"
                  class="i-lucide-check size-3 text-white"
                />
              </span>
              <span
                v-else
                class="w-4 h-4 shrink-0 rounded-full border flex items-center justify-center"
                :class="
                  isSelected(option) ? 'border-n-brand' : 'border-n-strong'
                "
              >
                <span
                  v-if="isSelected(option)"
                  class="w-2 h-2 rounded-full bg-n-brand"
                />
              </span>
              <span class="truncate text-n-slate-12">{{ option.label }}</span>
            </span>
            <span
              v-if="showBadge && option.type"
              class="shrink-0 px-2 py-0.5 text-xs font-medium rounded-full"
              :class="
                isDirect(option.type)
                  ? 'bg-n-teal-4 text-n-teal-11'
                  : 'bg-n-slate-4 text-n-slate-11'
              "
            >
              {{ typeLabel(option.type) }}
            </span>
          </li>
          <li
            v-if="filteredOptions.length === 0"
            class="px-3 py-2 text-sm text-n-slate-11"
          >
            {{ emptyState || t('CAMPAIGN.REPORT.LEADERBOARD.NO_DATA') }}
          </li>
        </ul>

        <!-- Selected tags (multiple only) -->
        <div
          v-if="multiple && selectedTags.length > 0"
          class="flex flex-wrap gap-1.5 px-3 py-2 border-t border-n-weak"
        >
          <span
            v-for="tag in selectedTags"
            :key="tag.value"
            class="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-n-alpha-black1"
          >
            <span
              v-if="showBadge && tag.type"
              class="w-1.5 h-1.5 rounded-full"
              :class="isDirect(tag.type) ? 'bg-n-teal-9' : 'bg-n-slate-9'"
            />
            <span class="text-xs truncate max-w-[8rem] text-n-slate-12">
              {{ tag.label }}
            </span>
            <span
              class="cursor-pointer i-lucide-x size-3 text-n-slate-11"
              @click.stop="removeTag(tag.value)"
            />
          </span>
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>
